import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { useUpdateMerchantSettings } from '@/api/domains/merchant-settings';
import type { PointSelectionMode } from '@/api/types/settings';
import { PlusBadge } from '@/components/plus-badge';
import { Radio } from '@/components/ui/radio';
import { toApiProblem, toastProblem } from '@/lib/errors';
import { cn } from '@/lib/utils';

const GROUP_NAME = 'point-selection-mode';

type ModeRowProps = {
  value: PointSelectionMode;
  title: string;
  right: ReactNode;
  selected: boolean;
  disabled?: boolean;
  onSelect: (value: PointSelectionMode) => void;
};

/** Compact radio-card row: dot + title + right-side hint or Plus badge. */
function ModeRow({ value, title, right, selected, disabled, onSelect }: ModeRowProps) {
  const inputId = `${GROUP_NAME}-${value}`;

  return (
    <label
      htmlFor={inputId}
      className={cn(
        'flex items-center gap-2.5 rounded-[10px] border px-3.5 py-3 transition-colors',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        // Same selected treatment as RadioGroupItem (1px muted-foreground, no
        // fill): a tinted background bleeds under the border on rounded corners.
        selected ? 'border-muted-foreground' : 'border-[#e5e5e5]',
        !disabled && !selected && 'hover:border-muted-foreground/60',
      )}
    >
      <Radio
        id={inputId}
        name={GROUP_NAME}
        value={value}
        checked={selected}
        disabled={disabled}
        onChange={() => onSelect(value)}
      />
      <span className="min-w-0 flex-1 text-sm font-medium text-black">{title}</span>
      {right}
    </label>
  );
}

interface PointModeSectionProps {
  mode: PointSelectionMode;
  isShopifyPlus: boolean;
}

/**
 * How buyers pick an InPost point at checkout. Saves immediately on selection;
 * the map-based modes need the API keys configured on the Authorization page,
 * so a hint links there when one of those modes is active.
 */
export function PointModeSection({ mode, isShopifyPlus }: PointModeSectionProps) {
  const { t } = useTranslation('settings');

  const update = useUpdateMerchantSettings();

  const [pendingMode, setPendingMode] = useState<PointSelectionMode | null>(null);
  const selectedMode = pendingMode ?? mode;

  useEffect(() => {
    if (pendingMode !== null && pendingMode === mode) {
      setPendingMode(null);
    }
  }, [pendingMode, mode]);

  const handleModeChange = (next: PointSelectionMode) => {
    if (next === mode || update.isPending) return;
    setPendingMode(next);
    update.mutate(
      { settings: { point_selection_mode: next } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => {
          setPendingMode(null);
          toastProblem(toApiProblem(error));
        },
      },
    );
  };

  const hint = (key: string) => (
    <span className="shrink-0 text-[13px] text-muted-foreground">{t(key)}</span>
  );

  return (
    <div className="py-4" role="radiogroup" aria-label={t('checkout.pointMode.title')}>
      <span className="block text-sm font-medium text-black">{t('checkout.pointMode.title')}</span>
      <div className="mt-2.5 flex flex-col gap-2">
        <ModeRow
          value="basic"
          title={t('checkout.pointMode.modes.basic.title')}
          right={hint('checkout.pointMode.modes.basic.hint')}
          selected={selectedMode === 'basic'}
          onSelect={handleModeChange}
        />
        <ModeRow
          value="checkout_ext"
          title={t('checkout.pointMode.modes.checkoutExt.title')}
          right={isShopifyPlus ? hint('checkout.pointMode.modes.checkoutExt.hint') : <PlusBadge />}
          selected={selectedMode === 'checkout_ext'}
          disabled={!isShopifyPlus}
          onSelect={handleModeChange}
        />
        <ModeRow
          value="thank_you"
          title={t('checkout.pointMode.modes.thankYou.title')}
          right={hint('checkout.pointMode.modes.thankYou.hint')}
          selected={selectedMode === 'thank_you'}
          onSelect={handleModeChange}
        />
      </div>
      {/* Always visible: switching to a map mode is rejected until the keys exist,
          so the pointer to where to add them must not depend on the selection. */}
      <p className="mt-2.5 text-[13px] text-muted-foreground">
        {t('checkout.pointMode.apiKeysHint')}{' '}
        <Link to="/authorization?highlight=api-keys" className="font-medium text-black underline">
          {t('checkout.pointMode.apiKeysLink')}
        </Link>
      </p>
    </div>
  );
}
